// 인증 관련 모듈
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  FacebookAuthProvider,
  GithubAuthProvider,
  signOut,
  updateProfile as updateFirebaseProfile,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, database, storage, requestNotificationPermission } from '../utils/firebase-config.js';
import { ref, get, set, onValue, update, push, remove } from 'firebase/database';

// UI 관련 모듈
import { showToast } from '../utils/ui-manager.js';
import { toggleTheme } from './theme-manager.js';
import { createPost, addEmbeddedPanelToPostData } from '../components/post.js';
import { updateProfile, uploadProfileImage } from '../components/user.js';
import { panelManager } from '../components/draggable-panel.js';
import tourGuide from './tour-guide.js';
import { checkForUpdates, setupPeriodicUpdateCheck } from './update-checker.js';
import { userCounter } from './user-counter.js';

/**
 * 모든 이벤트 리스너를 설정하는 함수
 */
export const setupEventListeners = () => {
  setupPanelEventListeners();
  setupSettingsEventListeners();
  setupDBPanelEventListeners();
  // 업데이트 확인 기능 초기화
  setupUpdateChecker();
  // 로그인 모달 관련 이벤트
  const loginModal = new bootstrap.Modal(document.getElementById('loginModal'));
  const emailLoginBtn = document.getElementById('email-login-btn');
  const loginBtnSidebar = document.querySelector('.login-btn-sidebar');
  
  // 네비게이션 로고 클릭 이벤트 - 새로고침
  const navbarBrand = document.querySelector('.navbar-brand');
  if (navbarBrand) {
    navbarBrand.addEventListener('click', (e) => {
      e.preventDefault();
      window.location.reload();
    });
  }
  
  // 가입 모달 관련
  const registerLink = document.getElementById('register-link');
  const registerLinkModal = document.getElementById('register-link-modal');
  const loginLinkModal = document.getElementById('login-link-modal');
  const registerModal = new bootstrap.Modal(document.getElementById('registerModal'));
  
  // 통합된 인증 버튼
  const authBtn = document.getElementById('auth-btn');
  
  // 테마 전환 버튼
  const themeToggleBtn = document.getElementById('theme-toggle');
  
  // 게시물 작성 관련
  const createPostBtn = document.getElementById('create-post-btn');
  const postContent = document.getElementById('post-content');
  const uploadImageBtn = document.getElementById('upload-image-btn');
  
  // 로그인 폼
  const loginForm = document.getElementById('login-form');
  
  // 회원가입 폼
  const registerForm = document.getElementById('register-form');
  
  // 프로필 편집 버튼
  const editProfileBtn = document.getElementById('edit-profile-btn');
  
  // 네비게이션 메뉴 이벤트 처리 (뒤로가기 동작 충돌 방지)
  const navLinks = document.querySelectorAll('.navbar-nav .nav-link, .nav-item a');
  navLinks.forEach(link => {
    link.addEventListener('click', () => {
      // 앱 내 네비게이션 플래그 설정 (window.appNavigation이 있는 경우)
      if (window.appNavigation) {
        window.appNavigation.internalNavigation = true;
        
        // 페이지 상태 업데이트 (홈 페이지로 간주)
        const pageName = link.getAttribute('href')?.replace('#', '') || 'page';
        window.appNavigation.lastState = `SNS3_${pageName}`;
        console.log('네비게이션 메뉴 클릭:', pageName);
        
        // 메뉴명 추출 및 토스트 메시지 표시
        const menuName = link.textContent?.trim() || '메뉴';
        showToast(`'${menuName}'(으)로 이동합니다.`, 'info');
      }
    });
  });
  
  // 사이드바 바로가기 메뉴 이벤트 처리
  const sidebarLinks = document.querySelectorAll('.card .list-group-item, .card a.text-decoration-none');
  sidebarLinks.forEach(link => {
    link.addEventListener('click', () => {
      // 앱 내 네비게이션 플래그 설정 (window.appNavigation이 있는 경우)
      if (window.appNavigation) {
        window.appNavigation.internalNavigation = true;
        
        // 페이지 상태 업데이트
        const pageName = link.getAttribute('href')?.replace('#', '') || 'page';
        window.appNavigation.lastState = `SNS3_${pageName}`;
        console.log('사이드바 링크 클릭:', pageName);
        
        // 메뉴명 추출 및 토스트 메시지 표시
        const menuName = link.textContent?.trim() || '바로가기';
        showToast(`'${menuName}' 바로가기로 이동합니다.`, 'info');
      }
    });
  });
  
  // 통합된 인증 버튼 클릭 이벤트
  if (authBtn) {
    authBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      
      // 현재 사용자가 로그인되어 있는지 확인
      if (auth.currentUser) {
        try {
          // Firebase 로그아웃
          await signOut(auth);
          
          showToast('로그아웃되었습니다.');
        } catch (error) {
          console.error('로그아웃 오류:', error);
          showToast('로그아웃 중 오류가 발생했습니다.', 'error');
        }
      } else {
        // 로그아웃 상태일 경우 로그인 모달 표시
        loginModal.show();
      }
    });
  }
  
  // 사이드바 로그인 버튼 클릭 이벤트
  if (loginBtnSidebar) {
    loginBtnSidebar.addEventListener('click', () => {
      loginModal.show();
    });
  }
  
  // 이메일 로그인 버튼 클릭 이벤트
  if (emailLoginBtn) {
    emailLoginBtn.addEventListener('click', () => {
      loginModal.show();
    });
  }
  
  // 로그인 폼 제출 이벤트
  if (loginForm) {
    loginForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const email = document.getElementById('email').value;
      const password = document.getElementById('password').value;
      
      try {
        // 로그인 처리
        await signInWithEmailAndPassword(auth, email, password);
        loginModal.hide();
        loginForm.reset();
        
        // FCM 토큰 저장
        await saveFCMToken();
      } catch (error) {
        console.error('이메일 로그인 실패:', error);
        showToast('로그인에 실패했습니다: ' + error.message, 'error');
      }
    });
  }
  
  // 회원가입 폼 제출 이벤트
  if (registerForm) {
    registerForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const name = document.getElementById('register-name').value;
      const email = document.getElementById('register-email').value;
      const password = document.getElementById('register-password').value;
      const passwordConfirm = document.getElementById('register-password-confirm').value;
      
      if (password !== passwordConfirm) {
        showToast('비밀번호가 일치하지 않습니다.', 'error');
        return;
      }
      
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        // 사용자 이름 업데이트
        await updateFirebaseProfile(userCredential.user, {
          displayName: name
        });
        
        registerModal.hide();
        registerForm.reset();
        showToast('회원가입이 완료되었습니다.');
      } catch (error) {
        console.error('회원가입 실패:', error);
        showToast('회원가입에 실패했습니다: ' + error.message, 'error');
      }
    });
  }
  
  // 가입 링크 클릭 이벤트
  if (registerLink) {
    registerLink.addEventListener('click', (e) => {
      e.preventDefault();
      loginModal.hide();
      registerModal.show();
    });
  }
  
  // 모달 내 가입 링크 클릭 이벤트
  if (registerLinkModal) {
    registerLinkModal.addEventListener('click', (e) => {
      e.preventDefault();
      loginModal.hide();
      registerModal.show();
    });
  }
  
  // 모달 내 로그인 링크 클릭 이벤트
  if (loginLinkModal) {
    loginLinkModal.addEventListener('click', (e) => {
      e.preventDefault();
      registerModal.hide();
      loginModal.show();
    });
  }
  
  // 테마 전환 버튼 클릭 이벤트
  if (themeToggleBtn) {
    themeToggleBtn.addEventListener('click', (e) => {
      // 이벤트 전파 방지
      e.preventDefault();
      e.stopPropagation();
      
      // 테마 전환 플래그 설정 (앱 종료 방지용)
      window.themeToggleClicked = true;
      
      // 히스토리 처리 (앱 종료 방지용)
      window.appNavigation.internalNavigation = true;
      
      // 테마 전환
      toggleTheme();
      
      // 히스토리 상태 복원
      setTimeout(() => {
        history.pushState({ page: 'home' }, '', window.location.href);
      }, 50);
    });
  }
  
  // 게시물 작성 버튼 클릭 이벤트
  if (createPostBtn && postContent) {
    createPostBtn.addEventListener('click', async () => {
      const content = postContent.value.trim();
      if (content) {
        // 선택된 이미지 파일이 있다면 함께 업로드
        const selectedImageFile = document.querySelector('#post-image-upload')?.files[0] || null;
        await createPost(content, selectedImageFile);
        postContent.value = '';
        
        // 이미지 미리보기 및 파일 선택 초기화
        if (document.querySelector('#post-image-preview')) {
          document.querySelector('#post-image-preview').innerHTML = '';
        }
        if (document.querySelector('#post-image-upload')) {
          document.querySelector('#post-image-upload').value = '';
        }
      } else {
        showToast('게시물 내용을 입력해주세요.', 'warning');
      }
    });
  }
  
  // 이미지 업로드 버튼 클릭 이벤트
  if (uploadImageBtn) {
    // 이미지 업로드를 위한 숨겨진 파일 입력 필드가 없으면 생성
    let fileInput = document.querySelector('#post-image-upload');
    
    if (!fileInput) {
      fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.id = 'post-image-upload';
      fileInput.accept = 'image/*';
      fileInput.style.display = 'none';
      document.body.appendChild(fileInput);
      
      // 미리보기 컨테이너 생성
      let previewContainer = document.createElement('div');
      previewContainer.id = 'post-image-preview';
      previewContainer.style.marginTop = '10px';
      
      // 미리보기 컨테이너를 게시물 작성 영역에 추가
      const postContentArea = postContent?.parentElement;
      if (postContentArea) {
        postContentArea.appendChild(previewContainer);
      }
    }
    
    // 업로드 버튼 클릭 시 파일 입력 필드 클릭
    uploadImageBtn.addEventListener('click', () => {
      if (!auth.currentUser) {
        showToast('로그인이 필요한 기능입니다.', 'warning');
        return;
      }
      fileInput.click();
    });
    
    // 파일 선택 시 미리보기 표시
    fileInput.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const previewContainer = document.querySelector('#post-image-preview');
          if (previewContainer) {
            previewContainer.innerHTML = `
              <div class="position-relative">
                <img src="${e.target.result}" alt="업로드 이미지 미리보기" class="img-fluid rounded mb-2" style="max-height: 200px;">
                <button type="button" class="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 remove-image">
                  <i class="bi bi-x"></i>
                </button>
              </div>
            `;
            
            // 이미지 제거 버튼에 이벤트 리스너 추가
            previewContainer.querySelector('.remove-image').addEventListener('click', () => {
              previewContainer.innerHTML = '';
              fileInput.value = '';
            });
          }
        };
        reader.readAsDataURL(file);
      }
    });
  }
  
  // 프로필 편집 버튼 클릭 이벤트
  if (editProfileBtn) {
    editProfileBtn.addEventListener('click', () => {
      // 현재 사용자 정보 확인
      const user = auth.currentUser;
      if (!user) {
        showToast('로그인이 필요한 기능입니다.', 'warning');
        return;
      }
      
      // 프로필 모달 표시
      const profileModal = new bootstrap.Modal(document.getElementById('profileModal'));
      
      // 현재 사용자 정보로 폼 초기화
      const profilePreview = document.getElementById('profile-preview');
      const profileBio = document.getElementById('profile-bio');
      const profileLocation = document.getElementById('profile-location');
      const profileWebsite = document.getElementById('profile-website');
      
      // 프로필 이미지 설정
      if (user.photoURL) {
        profilePreview.src = user.photoURL;
      } else {
        profilePreview.src = 'assets/default-avatar.png';
      }
      
      // Firebase에서 사용자 프로필 정보 가져오기
      const userProfileRef = ref(database, `users/${user.uid}/profile`);
      get(userProfileRef).then((snapshot) => {
        if (snapshot.exists()) {
          const profileData = snapshot.val();
          profileBio.value = profileData.bio || '';
          profileLocation.value = profileData.location || '';
          profileWebsite.value = profileData.website || '';
        }
      }).catch((error) => {
        console.error('프로필 정보 로드 실패:', error);
      });
      
      // 모달 표시
      profileModal.show();
    });
  }
  
  // 프로필 이미지 변경 이벤트
  const profileImage = document.getElementById('profile-image');
  if (profileImage) {
    profileImage.addEventListener('change', (e) => {
      if (e.target.files.length > 0) {
        const file = e.target.files[0];
        const profilePreview = document.getElementById('profile-preview');
        
        // 이미지 미리보기
        const reader = new FileReader();
        reader.onload = (e) => {
          profilePreview.src = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    });
  }
  
  // 프로필 폼 제출 이벤트
  const profileForm = document.getElementById('profile-form');
  if (profileForm) {
    profileForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const user = auth.currentUser;
      if (!user) {
        showToast('로그인이 필요한 기능입니다.', 'warning');
        return;
      }
      
      const bio = document.getElementById('profile-bio').value;
      const location = document.getElementById('profile-location').value;
      const website = document.getElementById('profile-website').value;
      const imageFile = document.getElementById('profile-image').files[0];
      
      try {
        // 프로필 정보 업데이트
        await updateProfile(user.uid, {
          bio,
          location,
          website
        });
        
        // 이미지가 변경된 경우
        if (imageFile) {
          await uploadProfileImage(user.uid, imageFile);
        }
        
        // 모달 닫기
        const profileModal = bootstrap.Modal.getInstance(document.getElementById('profileModal'));
        profileModal.hide();
        
        showToast('프로필이 업데이트되었습니다.', 'success');
      } catch (error) {
        console.error('프로필 업데이트 실패:', error);
        showToast('프로필 업데이트에 실패했습니다: ' + error.message, 'error');
      }
    });
  }
  
  // 소셜 로그인 버튼 이벤트 리스너 설정
  const setupSocialLoginButtons = () => {
    // 메인 화면의 구글 로그인 버튼
    const googleLoginBtn = document.getElementById('google-login-btn-main');
    if (googleLoginBtn) {
      googleLoginBtn.addEventListener('click', handleGoogleLogin);
    }
    
    // 로그인 모달 내 구글 로그인 버튼
    const modalGoogleLoginBtn = document.getElementById('google-login-btn-modal');
    if (modalGoogleLoginBtn) {
      modalGoogleLoginBtn.addEventListener('click', handleGoogleLogin);
    }
  };
  
  // 구글 로그인 처리 함수
  const handleGoogleLogin = async () => {
    try {
      const authProvider = new GoogleAuthProvider();
      authProvider.addScope('email');
      authProvider.addScope('profile');
      
      // 구글 로그인 처리
      await signInWithPopup(auth, authProvider);
      
      // 모달 닫기
      const loginModal = bootstrap.Modal.getInstance(document.getElementById('loginModal'));
      if (loginModal) {
        loginModal.hide();
      }
      
      // FCM 토큰 저장
      await saveFCMToken();
    } catch (error) {
      console.error('구글 로그인 실패:', error);
      showToast('구글 로그인에 실패했습니다: ' + error.message, 'error');
    }
  };

  // 소셜 로그인 버튼 설정 함수 호출
  setupSocialLoginButtons();
  
  console.log('모든 이벤트 리스너가 설정되었습니다.');

  // 투어 가이드 설정
  setupTourGuideListeners();
  
  // 인증 상태 변경 리스너
  onAuthStateChanged(auth, (user) => {
    if (user) {
      // 사용자가 로그인한 경우, 신규 사용자라면 투어 가이드 표시
      const isNewUser = user.metadata.creationTime === user.metadata.lastSignInTime;
      
      if (isNewUser) {
        // 신규 사용자인 경우, 로그인 후 1초 후에 홈 투어 시작
        setTimeout(() => {
          // 새 사용자는 자동 진행 모드로 투어 시작 (true로 설정)
          tourGuide.startHomeTour(true);
        }, 1000);
      }
    }
  });

  // 기기 목록 FAB 버튼 이벤트 리스너 추가
  const deviceBtn = document.getElementById('fab-device-button');
  if (deviceBtn) {
    deviceBtn.addEventListener('click', async (e) => {
      e.stopPropagation(); // 이벤트 버블링 방지
      
      // 버튼 애니메이션 효과
      deviceBtn.classList.add('active');
      setTimeout(() => {
        deviceBtn.classList.remove('active');
      }, 500);
      
      // 토스트 메시지 표시
      showToast('기기 목록을 불러오는 중입니다...', 'info');
      
      // 새 패널 생성
      const panel = createNewPanel('');
      
      // 패널 제목 변경
      panel.setTitle('기기 목록');
      
      try {
        // Firebase DEVICE 노드 참조 생성
        const deviceRef = ref(database, 'DEVICE');
        
        // 패널 내용 설정
        const iframe = panel.iframe;
        if (iframe && iframe.contentWindow) {
          // 현재 활성화된 기기 리스너 (해제하기 위함)
          let activeDeviceListener = null;
          let activeDeviceKey = null;
          
          // 초기 기기 목록 가져오기
          iframe.addEventListener('load', async () => {
            try {
              const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
              
              // 스타일 설정
              const style = iframeDoc.createElement('style');
              style.textContent = `
                body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 0; }
                .table { width: 100%; border-collapse: collapse; box-shadow: 0 2px 8px rgba(0,0,0,0.1); border-radius: 5px; overflow: hidden; }
                .table th { background-color: #f0f2f5; color: #3B5998; padding: 10px; text-align: left; font-weight: 600; }
                .table td { padding: 10px; border-top: 1px solid #e4e6eb; }
                .table tr:hover { background-color: #f5f7fa; cursor: pointer; }
                .badge { padding: 5px 8px; border-radius: 30px; display: inline-block; font-weight: 500; font-size: 12px; }
                .badge.bg-success { background-color: #28a745; color: white; }
                .badge.bg-danger { background-color: #dc3545; color: white; }
                .badge.bg-secondary { background-color: #6c757d; color: white; }
                .text-muted { color: #6c757d; }
                h4 { color: #3B5998; margin-bottom: 15px; }
                .table-active { background-color: #ecf3ff !important; }
                .text-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                
                /* 다크 모드 지원 */
                @media (prefers-color-scheme: dark) {
                  body { background-color: #18191a; color: #e4e6eb; }
                  .table { background-color: #242526; }
                  .table th { background-color: #3A3B3C; color: #e4e6eb; }
                  .table td { border-top-color: #3E4042; }
                  .table tr:hover { background-color: #3A3B3C; }
                  .text-muted { color: #B0B3B8; }
                  h4 { color: #E4E6EB; }
                  .table-active { background-color: #3e5070 !important; }
                }
                
                /* 스피너 스타일 */
                .spinner-container { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; }
                .spinner-border { width: 3rem; height: 3rem; border-width: 0.25em; }
                .spinner-border.text-primary { border-color: #3B5998; border-right-color: transparent; }
                
                /* 데이터 변경 애니메이션 */
                @keyframes highlightChange {
                  0% { background-color: rgba(255, 246, 200, 0.7); }
                  100% { background-color: rgba(190, 190, 190, 0.3); }
                }
                
                .highlight-change {
                  animation: highlightChange 2s ease-out forwards;
                }
                
                /* 다크 모드에서 데이터 변경 애니메이션 */
                @media (prefers-color-scheme: dark) {
                  @keyframes highlightChangeDark {
                    0% { background-color: rgba(255, 246, 200, 0.4); }
                    100% { background-color: rgba(50, 50, 50, 0.5); }
                  }
                  
                  .highlight-change {
                    animation: highlightChangeDark 2s ease-out forwards;
                  }
                }
                
                /* 디바이스 상세 정보 디스플레이 */
                .device-detail-container {
                  padding: 20px;
                }
                .detail-header {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 20px;
                }
                .back-button {
                  background-color: #f0f2f5;
                  border: none;
                  padding: 8px 16px;
                  border-radius: 20px;
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  gap: 6px;
                  transition: all 0.2s;
                }
                .back-button:hover {
                  background-color: #e4e6eb;
                }
                .device-info {
                  margin-bottom: 30px;
                }
                .device-title {
                  font-size: 24px;
                  font-weight: 500;
                  margin-bottom: 5px;
                }
                .device-id {
                  font-size: 14px;
                  color: #666;
                  margin-bottom: 15px;
                  word-break: break-all;
                }
                .info-grid {
                  display: grid;
                  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
                  gap: 15px;
                  margin-top: 20px;
                }
                .info-card {
                  background-color: #f9f9f9;
                  border-radius: 10px;
                  padding: 15px;
                  transition: transform 0.2s ease;
                }
                .info-card:hover {
                  transform: translateY(-3px);
                }
                .info-label {
                  font-size: 14px;
                  color: #666;
                  margin-bottom: 5px;
                }
                .info-value {
                  font-size: 18px;
                  font-weight: 500;
                }
                
                /* 다크 모드 */
                @media (prefers-color-scheme: dark) {
                  .back-button {
                    background-color: #3A3B3C;
                    color: #e4e6eb;
                  }
                  .back-button:hover {
                    background-color: #4E4F50;
                  }
                  .device-id {
                    color: #B0B3B8;
                  }
                  .info-card {
                    background-color: #242526;
                  }
                  .info-label {
                    color: #B0B3B8;
                  }
                }
              `;
              
              iframeDoc.head.appendChild(style);
              
              // 초기 로딩 화면 표시 - 스피너 사용
              iframeDoc.body.innerHTML = `
                <div class="spinner-container">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">로딩 중...</span>
                  </div>
                  <p class="mt-3">기기 목록을 불러오는 중입니다...</p>
                </div>
              `;
              
              // 테이블 구조 초기화
              const initializeTable = () => {
                const tableHtml = `
                  <div class="p-3">
                    <h4 class="mb-3">기기 목록</h4>
                    <div class="table-responsive">
                      <table class="table table-hover">
                        <thead>
                          <tr>
                            <th scope="col">상태</th>
                            <th scope="col">기기명</th>
                            <th scope="col">ID</th>
                            <th scope="col">마지막 활동</th>
                          </tr>
                        </thead>
                        <tbody id="device-table-body">
                          <!-- 기기 목록이 여기에 추가됩니다 -->
                          <tr>
                            <td colspan="4" class="text-center">기기 목록을 불러오는 중입니다...</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p class="text-muted small mt-3">행을 클릭하면 해당 기기의 실시간 정보를 확인할 수 있습니다.</p>
                  </div>
                `;
                
                iframeDoc.body.innerHTML = tableHtml;
                return iframeDoc.getElementById('device-table-body');
              };
              
              // 초기 테이블 초기화
              const tableBody = initializeTable();
              
              // 초기 기기 목록 가져오기 (한 번만)
              const snapshot = await get(deviceRef);
              
              if (snapshot.exists()) {
                // 초기 테이블 비우기
                tableBody.innerHTML = '';
                
                // 모든 기기 정보를 테이블에 추가 (리스너 없이)
                snapshot.forEach((childSnapshot) => {
                  const deviceKey = childSnapshot.key;
                  const deviceData = childSnapshot.val();
                  
                  // 기기 행 생성 및 추가 (isUpdate = false)
                  addDeviceRow(tableBody, deviceKey, deviceData);
                });
                
                // 행 클릭 이벤트 설정
                setupRowClickEvents(iframeDoc);
              } else {
                // 데이터가 없을 경우
                tableBody.innerHTML = `
                  <tr>
                    <td colspan="4" class="text-center">
                      <div class="p-3">
                        <i class="bi bi-exclamation-circle fs-4 text-muted"></i>
                        <p class="mt-2">등록된 기기가 없거나 접근 권한이 없습니다.</p>
                      </div>
                    </td>
                  </tr>
                `;
              }
              
              showToast('기기 목록이 로드되었습니다.', 'success');
            } catch (error) {
              console.error('iframe 초기화 중 오류:', error);
              showError(iframe, error);
            }
          });
          
          // 기기 행 추가 함수
          const addDeviceRow = (tableBody, deviceKey, deviceData) => {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            if (!iframeDoc) return;
            
            // 기기 상태에 따른 배지 색상 설정
            let statusBadge = '<span class="badge bg-secondary">알 수 없음</span>';
            if (deviceData.status) {
              if (deviceData.status === 'online') {
                statusBadge = '<span class="badge bg-success">온라인</span>';
              } else if (deviceData.status === 'offline') {
                statusBadge = '<span class="badge bg-danger">오프라인</span>';
              }
            }
            
            // 마지막 활동 시간 포맷팅
            const lastActive = deviceData.lastActive 
              ? new Date(deviceData.lastActive).toLocaleString() 
              : '정보 없음';
            
            // 새 행 추가
            const newRow = iframeDoc.createElement('tr');
            newRow.className = 'device-row';
            newRow.setAttribute('data-device-id', deviceKey);
            newRow.setAttribute('data-device-name', deviceData.name || deviceKey);
            
            newRow.innerHTML = `
              <td>${statusBadge}</td>
              <td>${deviceData.name || deviceKey}</td>
              <td class="text-truncate" style="max-width: 150px;">${deviceKey}</td>
              <td>${lastActive}</td>
            `;
            
            tableBody.appendChild(newRow);
          };
          
          // 행 클릭 이벤트 설정 함수
          const setupRowClickEvents = (iframeDoc) => {
            const deviceRows = iframeDoc.querySelectorAll('.device-row');
            deviceRows.forEach(row => {
              // 기존 이벤트 리스너 제거 (중복 방지)
              const newRow = row.cloneNode(true);
              row.parentNode.replaceChild(newRow, row);
              
              newRow.addEventListener('click', () => {
                const deviceId = newRow.getAttribute('data-device-id');
                const deviceName = newRow.getAttribute('data-device-name');
                
                // 기존 리스너 해제
                if (activeDeviceListener) {
                  activeDeviceListener();
                  activeDeviceListener = null;
                  activeDeviceKey = null;
                }
                
                // 기기 상세 정보 화면으로 전환
                showDeviceDetail(deviceId, deviceName);
              });
            });
          };
          
          // 기기 상세 정보 표시 함수
          const showDeviceDetail = (deviceId, deviceName) => {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            if (!iframeDoc) return;
            
            // 로딩 표시
            iframeDoc.body.innerHTML = `
              <div class="spinner-container">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">로딩 중...</span>
                </div>
                <p class="mt-3">기기 정보를 불러오는 중입니다...</p>
              </div>
            `;
            
            // 패널 제목 업데이트
            panel.setTitle(`기기 정보: ${deviceName}`);
            
            // 디바이스 상세 정보 초기 UI 설정
            const initializeDetailView = () => {
              return `
                <div class="device-detail-container">
                  <div class="detail-header">
                    <button class="back-button" id="back-to-list">
                      <span>←</span> 목록으로 돌아가기
                    </button>
                    <div>
                      <span class="badge bg-success" id="device-status-badge">실시간 업데이트</span>
                    </div>
                  </div>
                  
                  <div class="device-info">
                    <h2 class="device-title" id="device-name">${deviceName}</h2>
                    <div class="device-id" id="device-id">${deviceId}</div>
                  </div>
                  
                  <div id="device-data-container">
                    <div class="info-grid" id="info-grid">
                      <!-- 여기에 디바이스 정보가 실시간으로 표시됩니다 -->
                      <div class="info-card">
                        <div class="info-label">데이터</div>
                        <div class="info-value">로딩 중...</div>
                      </div>
                    </div>
                  </div>
                </div>
              `;
            };
            
            // 디바이스 상세 정보 렌더링
            const renderDeviceData = (deviceData) => {
              const infoGrid = iframeDoc.getElementById('info-grid');
              if (!infoGrid) return;
              
              // 데이터 전처리
              const statusBadge = iframeDoc.getElementById('device-status-badge');
              if (deviceData.status === 'online') {
                statusBadge.className = 'badge bg-success';
                statusBadge.textContent = '온라인 (실시간)';
              } else if (deviceData.status === 'offline') {
                statusBadge.className = 'badge bg-danger';
                statusBadge.textContent = '오프라인 (실시간)';
              } else {
                statusBadge.className = 'badge bg-secondary';
                statusBadge.textContent = '알 수 없음 (실시간)';
              }
              
              // 그리드 비우기
              infoGrid.innerHTML = '';
              
              // 기본 정보 추가
              if (deviceData.lastActive) {
                addInfoCard(infoGrid, '마지막 활동', new Date(deviceData.lastActive).toLocaleString());
              }
              
              // 모든 데이터 키 순회하여 표시
              Object.entries(deviceData).forEach(([key, value]) => {
                if (key !== 'name' && key !== 'status') { // 이미 표시된 정보는 제외
                  // 객체인 경우 JSON 문자열로 변환
                  let displayValue = value;
                  if (typeof value === 'object' && value !== null) {
                    // 중첩된 객체인 경우 각각 추가
                    Object.entries(value).forEach(([subKey, subValue]) => {
                      addInfoCard(infoGrid, `${key}.${subKey}`, formatValue(subValue));
                    });
                    return; // 중첩 객체는 직접 추가하지 않음
                  }
                  
                  addInfoCard(infoGrid, key, formatValue(value));
                }
              });
              
              // 데이터가 없는 경우 메시지 표시
              if (infoGrid.children.length === 0) {
                infoGrid.innerHTML = `
                  <div class="info-card">
                    <div class="info-label">알림</div>
                    <div class="info-value">추가 데이터가 없습니다</div>
                  </div>
                `;
              }
              
              // 하이라이트 효과 적용
              infoGrid.classList.remove('highlight-change');
              void infoGrid.offsetWidth; // reflow 강제로 일으켜 애니메이션 재설정
              infoGrid.classList.add('highlight-change');
            };
            
            // 정보 카드 추가 함수
            const addInfoCard = (container, label, value) => {
              const card = iframeDoc.createElement('div');
              card.className = 'info-card';
              card.innerHTML = `
                <div class="info-label">${label}</div>
                <div class="info-value">${value}</div>
              `;
              container.appendChild(card);
            };
            
            // 값 포맷팅 함수
            const formatValue = (value) => {
              if (value === null || value === undefined) {
                return '없음';
              }
              
              if (typeof value === 'boolean') {
                return value ? '예' : '아니오';
              }
              
              if (typeof value === 'number') {
                // 소수점 처리
                return Number.isInteger(value) ? value : value.toFixed(2);
              }
              
              if (typeof value === 'object') {
                try {
                  return JSON.stringify(value);
                } catch (e) {
                  return '객체';
                }
              }
              
              return value;
            };
            
            // 상세 정보 UI 초기화
            iframeDoc.body.innerHTML = initializeDetailView();
            
            // 목록으로 돌아가기 버튼 이벤트 설정
            const backButton = iframeDoc.getElementById('back-to-list');
            if (backButton) {
              backButton.addEventListener('click', () => {
                // 기존 리스너 해제
                if (activeDeviceListener) {
                  activeDeviceListener();
                  activeDeviceListener = null;
                  activeDeviceKey = null;
                }
                
                // 패널 제목 복원
                panel.setTitle('기기 목록');
                
                // 테이블 초기화 및 데이터 다시 로드
                iframe.src = 'about:blank';
              });
            }
            
            // 디바이스 노드 참조 생성
            const deviceItemRef = ref(database, `DEVICE/${deviceId}`);
            
            // 상세 정보 실시간 리스너 설정
            activeDeviceKey = deviceId;
            activeDeviceListener = onValue(deviceItemRef, (snapshot) => {
              if (snapshot.exists()) {
                const deviceData = snapshot.val();
                console.log('실시간 기기 데이터:', deviceId);
                
                // 기기 데이터 렌더링
                renderDeviceData(deviceData);
              } else {
                // 기기가 삭제된 경우
                const deviceDataContainer = iframeDoc.getElementById('device-data-container');
                if (deviceDataContainer) {
                  deviceDataContainer.innerHTML = `
                    <div class="p-3 text-center">
                      <i class="bi bi-exclamation-triangle text-danger fs-1"></i>
                      <h5 class="mt-3">기기가 삭제되었습니다</h5>
                      <p class="text-muted">이 기기의 데이터를 더 이상 사용할 수 없습니다.</p>
                    </div>
                  `;
                }
                
                const statusBadge = iframeDoc.getElementById('device-status-badge');
                if (statusBadge) {
                  statusBadge.className = 'badge bg-danger';
                  statusBadge.textContent = '삭제됨';
                }
              }
            });
          };
          
          // 오류 표시 함수
          const showError = (iframe, error) => {
            try {
              const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
              iframeDoc.body.innerHTML = `
                <div class="p-3 text-center">
                  <i class="bi bi-exclamation-triangle text-danger fs-1"></i>
                  <h5 class="mt-3">오류가 발생했습니다</h5>
                  <p class="text-muted">${error.message}</p>
                </div>
              `;
            } catch (e) {
              console.error('iframe 오류 메시지 설정 중 추가 오류:', e);
            }
          };
          
          // 패널이 닫힐 때 모든 리스너 해제
          const originalClose = panel.close;
          panel.close = function(fromPanelManager = false) {
            // 활성화된 기기 리스너 해제
            if (activeDeviceListener) {
              activeDeviceListener();
              activeDeviceListener = null;
              activeDeviceKey = null;
              console.log('기기 리스너가 해제되었습니다.');
            }
            
            // 원래 close 함수 호출
            return originalClose.call(this, fromPanelManager);
          };
          
          // iframe 새로고침
          iframe.src = 'about:blank';
        }
      } catch (error) {
        console.error('기기 목록 로드 중 오류:', error);
        showToast('기기 목록을 불러오는 중 오류가 발생했습니다.', 'error');
        
        // 오류 메시지를 패널에 표시
        if (panel && panel.iframe) {
          const iframe = panel.iframe;
          iframe.addEventListener('load', () => {
            try {
              const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
              iframeDoc.body.innerHTML = `
                <div class="p-3 text-center">
                  <i class="bi bi-exclamation-triangle text-danger fs-1"></i>
                  <h5 class="mt-3">오류가 발생했습니다</h5>
                  <p class="text-muted">${error.message}</p>
                </div>
              `;
            } catch (e) {
              console.error('iframe 오류 메시지 설정 중 추가 오류:', e);
            }
          });
          iframe.src = 'about:blank';
        }
      }
    });
  }
};

/**
 * 설정 모달 관련 이벤트 리스너 설정
 */
const setupSettingsEventListeners = () => {
  // 설정 모달 열기 버튼
  const openSettingsBtn = document.getElementById('open-settings-modal');
  const settingsModal = new bootstrap.Modal(document.getElementById('settingsModal'));
  
  // 설정 저장 버튼
  const saveSettingsBtn = document.getElementById('saveSettingsBtn');
  
  // 다크 모드 스위치
  const darkModeSwitch = document.getElementById('darkModeSwitch');
  
  // 알림 스위치
  const notificationSwitch = document.getElementById('notificationSwitch');
  
  // 클라우드 메시지 테스트 버튼
  const cloudMessageTestBtn = document.getElementById('cloudMessageTestBtn');
  
  // 캐시 비우기 버튼
  const clearCacheBtn = document.getElementById('clearCacheBtn');
  
  // 설정 초기화 버튼
  const resetSettingsBtn = document.getElementById('resetSettingsBtn');
  
  // 설정 투어 버튼
  const startSettingsTourBtn = document.getElementById('start-settings-tour-btn');
  
  // 설정 모달 열기
  if (openSettingsBtn) {
    openSettingsBtn.addEventListener('click', () => {
      // 현재 설정 상태를 가져와서 UI에 반영
      const isDarkMode = document.body.classList.contains('dark-mode');
      if (darkModeSwitch) {
        darkModeSwitch.checked = isDarkMode;
      }
      
      settingsModal.show();
    });
  }
  
  // 설정 투어 버튼 클릭 이벤트
  if (startSettingsTourBtn) {
    startSettingsTourBtn.addEventListener('click', () => {
      // 투어 가이드 모듈 가져오기
      import('./tour-guide.js').then(module => {
        const tourGuide = module.default;
        // 자동 진행 모드로 투어 시작 (true)
        tourGuide.startSettingsTour(true);
      }).catch(error => {
        console.error('투어 가이드 모듈 로드 실패:', error);
      });
    });
  }
  
  // 다크 모드 스위치 변경
  if (darkModeSwitch) {
    darkModeSwitch.addEventListener('change', () => {
      if (darkModeSwitch.checked) {
        document.body.classList.add('dark-mode');
        localStorage.setItem('theme', 'dark');
      } else {
        document.body.classList.remove('dark-mode');
        localStorage.setItem('theme', 'light');
      }
      
      // 테마 변경 함수 호출 (기존 기능 활용)
      toggleTheme();
    });
  }
  
  // 알림 스위치 변경
  if (notificationSwitch) {
    notificationSwitch.addEventListener('change', async () => {
      if (notificationSwitch.checked) {
        try {
          await requestNotificationPermission();
          showToast('알림이 활성화되었습니다.', 'success');
        } catch (error) {
          console.error('알림 권한 요청 실패:', error);
          showToast('알림 권한을 얻지 못했습니다.', 'error');
          notificationSwitch.checked = false;
        }
      } else {
        showToast('알림이 비활성화되었습니다.', 'info');
        // 알림 설정을 사용자 프로필에 저장할 수 있음
      }
    });
  }
  
  // 클라우드 메시지 테스트 버튼
  if (cloudMessageTestBtn) {
    cloudMessageTestBtn.addEventListener('click', async () => {
      try {
        // 테스트 메시지 전송 (실제로는 서버가 필요)
        showToast('클라우드 메시지 테스트를 시작합니다...', 'info');
        
        // FCM 토큰 가져오기
        const fcmToken = await requestNotificationPermission();
        if (!fcmToken) {
          showToast('FCM 토큰을 가져올 수 없습니다. 알림 권한을 확인하세요.', 'error');
          return;
        }

        // 사용자가 로그인되어 있는지 확인
        const user = auth.currentUser;
        if (!user) {
          showToast('테스트하려면 로그인이 필요합니다.', 'warning');
          return;
        }
        
        // Firebase 데이터베이스에 테스트 알림 요청 기록
        const testRef = ref(database, `notifications/test/${user.uid}`);
        const testData = {
          userId: user.uid,
          message: '테스트 알림 메시지입니다.',
          deviceToken: fcmToken,
          timestamp: Date.now()
        };
        await set(testRef, testData);
        
        console.log('테스트용 FCM 토큰:', fcmToken);
        
        // 로컬 테스트를 위한 알림 시뮬레이션
        // 실제 서버 호출 대신 로컬에서 직접 알림을 시뮬레이션
        try {
          // 토스트 메시지로 테스트 알림 표시
          const testTitle = 'FCM 테스트 알림';
          const testBody = '이 메시지가 보이면 FCM 수신 처리가 정상적으로 작동하는 것입니다.';
          
          // 새로운 FCM 토스트 스타일 사용 (상단에 간략하게 표시)
          const importedModule = await import('../utils/firebase-config.js');
          if (typeof importedModule.showFcmToast === 'function') {
            importedModule.showFcmToast(testTitle, testBody, 10000);
          } else {
            // showFcmToast 함수가 없으면 직접 구현해서 호출
            const showFcmToast = (title, body, duration = 10000) => {
              // 이미 존재하는 FCM 토스트 컨테이너를 찾거나 새로 생성
              let toastContainer = document.querySelector('.fcm-toast-container');
              
              if (!toastContainer) {
                toastContainer = document.createElement('div');
                toastContainer.className = 'fcm-toast-container position-fixed top-0 end-0 p-1';
                toastContainer.style.zIndex = '1070'; // Bootstrap 모달보다 위에 표시
                document.body.appendChild(toastContainer);
              }
              
              // 고유 ID 생성
              const toastId = `fcm-toast-${Date.now()}`;
              
              // 간소화된 토스트 엘리먼트 생성
              const toastHtml = `
                <div id="${toastId}" class="toast show" role="alert" aria-live="assertive" aria-atomic="true">
                  <div class="toast-header bg-primary text-white py-2">
                    <i class="bi bi-bell me-2"></i>
                    <strong class="me-auto">${title}</strong>
                    <button type="button" class="btn-close btn-close-white" onclick="document.getElementById('${toastId}').remove()"></button>
                  </div>
                  <div class="toast-body py-2">
                    ${body}
                  </div>
                </div>
              `;
              
              // 토스트 추가
              toastContainer.insertAdjacentHTML('beforeend', toastHtml);
              
              // 토스트 엘리먼트
              const toastElement = document.getElementById(toastId);
              
              // 스타일 추가 - 폭 제한 및 그림자 효과
              toastElement.style.maxWidth = '350px';
              toastElement.style.boxShadow = '0 0.5rem 1rem rgba(0, 0, 0, 0.15)';
              
              // 지정된 시간 후 DOM에서 제거
              setTimeout(() => {
                if (toastElement && toastElement.parentNode) {
                  // 페이드 아웃 효과
                  toastElement.classList.add('fade');
                  
                  // 완전히 제거
                  setTimeout(() => {
                    if (toastElement.parentNode) {
                      toastElement.parentNode.removeChild(toastElement);
                    }
                    
                    // 컨테이너에 더 이상 토스트가 없으면 컨테이너도 제거
                    if (toastContainer && toastContainer.children.length === 0) {
                      toastContainer.remove();
                    }
                  }, 300);
                }
              }, duration);
            };
            
            showFcmToast(testTitle, testBody, 10000);
          }
          
          showToast('로컬 FCM 테스트가 완료되었습니다. 실제 서버 연동은 서버 설정이 필요합니다.', 'success');
        } catch (error) {
          console.error('로컬 FCM 테스트 실패:', error);
          showToast(`로컬 FCM 테스트에 실패했습니다: ${error.message}`, 'error');
        }
        
        /* CORS 오류로 인해 주석 처리
        // Cloud Functions 엔드포인트 (서버 측에서 이 함수가 구현되어 있어야 함)
        const sendTestFcmEndpoint = 'https://us-central1-kdindoor-1ac7f.cloudfunctions.net/sendTestNotification';
        
        // FCM 테스트 알림 전송 요청
        const response = await fetch(sendTestFcmEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await auth.currentUser.getIdToken()}`
          },
          mode: 'no-cors', // CORS 정책 무시 (응답을 읽을 수 없게 됨)
          body: JSON.stringify({
            token: fcmToken,
            notification: {
              title: 'FCM 테스트 알림',
              body: '이 메시지가 보이면 FCM이 정상적으로 작동하는 것입니다.'
            },
            data: {
              type: 'test_notification',
              timestamp: Date.now().toString()
            }
          })
        });
        
        // no-cors 모드에서는 response.ok 등의 속성을 읽을 수 없음
        console.log('FCM 테스트 요청이 전송되었습니다.');
        */
      } catch (error) {
        console.error('클라우드 메시지 테스트 실패:', error);
        showToast(`테스트 메시지 전송에 실패했습니다: ${error.message}`, 'error');
      }
    });
  }
  
  // 캐시 비우기 버튼
  if (clearCacheBtn) {
    clearCacheBtn.addEventListener('click', async () => {
      try {
        // 캐시 비우기 확인
        if (confirm('캐시를 비우시겠습니까? 앱 데이터가 초기화될 수 있습니다.')) {
          // 브라우저의 캐시 스토리지 비우기
          if ('caches' in window) {
            const cacheNames = await window.caches.keys();
            await Promise.all(
              cacheNames.map(cacheName => window.caches.delete(cacheName))
            );
          }
          
          // localStorage 비우기 (설정 제외)
          const theme = localStorage.getItem('theme');
          localStorage.clear();
          if (theme) {
            localStorage.setItem('theme', theme);
          }
          
          showToast('캐시가 성공적으로 비워졌습니다.', 'success');
        }
      } catch (error) {
        console.error('캐시 비우기 실패:', error);
        showToast('캐시 비우기에 실패했습니다.', 'error');
      }
    });
  }
  
  // 설정 초기화 버튼
  if (resetSettingsBtn) {
    resetSettingsBtn.addEventListener('click', () => {
      if (confirm('모든 설정을 기본값으로 초기화하시겠습니까?')) {
        // 다크 모드 초기화
        if (darkModeSwitch) {
          darkModeSwitch.checked = false;
          document.body.classList.remove('dark-mode');
          localStorage.setItem('theme', 'light');
          toggleTheme();
        }
        
        // 알림 설정 초기화
        if (notificationSwitch) {
          notificationSwitch.checked = true;
        }
        
        // 언어 설정 초기화
        const languageSelect = document.getElementById('languageSelect');
        if (languageSelect) {
          languageSelect.value = 'ko';
        }
        
        // 글꼴 크기 초기화
        const fontSizeSelect = document.getElementById('fontSizeSelect');
        if (fontSizeSelect) {
          fontSizeSelect.value = 'normal';
          document.body.style.fontSize = ''; // 기본 크기로 재설정
        }
        
        // 메뉴 설정 초기화
        const menuCheckboxes = document.querySelectorAll('#menu-tab-pane input[type="checkbox"]');
        menuCheckboxes.forEach(checkbox => {
          checkbox.checked = true;
        });
        
        showToast('모든 설정이 초기화되었습니다.', 'success');
      }
    });
  }
  
  // 설정 저장 버튼
  if (saveSettingsBtn) {
    saveSettingsBtn.addEventListener('click', () => {
      try {
        // 언어 설정 저장
        const languageSelect = document.getElementById('languageSelect');
        if (languageSelect) {
          localStorage.setItem('language', languageSelect.value);
        }
        
        // 글꼴 크기 저장 및 적용
        const fontSizeSelect = document.getElementById('fontSizeSelect');
        if (fontSizeSelect) {
          localStorage.setItem('fontSize', fontSizeSelect.value);
          
          // 글꼴 크기 적용
          switch (fontSizeSelect.value) {
            case 'normal':
              document.body.style.fontSize = '';
              break;
            case 'large':
              document.body.style.fontSize = '1.1rem';
              break;
            case 'larger':
              document.body.style.fontSize = '1.2rem';
              break;
            case 'largest':
              document.body.style.fontSize = '1.3rem';
              break;
          }
        }
        
        // 메뉴 설정 저장
        const menuSettings = {};
        const menuCheckboxes = document.querySelectorAll('#menu-tab-pane input[type="checkbox"]');
        menuCheckboxes.forEach(checkbox => {
          menuSettings[checkbox.id] = checkbox.checked;
        });
        localStorage.setItem('menuSettings', JSON.stringify(menuSettings));
        
        // 비디오 자동 재생 설정 저장
        const autoPlaySwitch = document.getElementById('autoPlaySwitch');
        if (autoPlaySwitch) {
          localStorage.setItem('autoPlay', autoPlaySwitch.checked ? 'true' : 'false');
        }
        
        settingsModal.hide();
        showToast('설정이 저장되었습니다.', 'success');
      } catch (error) {
        console.error('설정 저장 실패:', error);
        showToast('설정 저장에 실패했습니다.', 'error');
      }
    });
  }
  
  // 설정 불러오기 (페이지 로드 시)
  const loadSettings = () => {
    try {
      // 언어 설정 불러오기
      const savedLanguage = localStorage.getItem('language');
      const languageSelect = document.getElementById('languageSelect');
      if (savedLanguage && languageSelect) {
        languageSelect.value = savedLanguage;
      }
      
      // 글꼴 크기 불러오기 및 적용
      const savedFontSize = localStorage.getItem('fontSize');
      const fontSizeSelect = document.getElementById('fontSizeSelect');
      if (savedFontSize && fontSizeSelect) {
        fontSizeSelect.value = savedFontSize;
        
        // 글꼴 크기 적용
        switch (savedFontSize) {
          case 'normal':
            document.body.style.fontSize = '';
            break;
          case 'large':
            document.body.style.fontSize = '1.1rem';
            break;
          case 'larger':
            document.body.style.fontSize = '1.2rem';
            break;
          case 'largest':
            document.body.style.fontSize = '1.3rem';
            break;
        }
      }
      
      // 메뉴 설정 불러오기
      const savedMenuSettings = localStorage.getItem('menuSettings');
      if (savedMenuSettings) {
        const menuSettings = JSON.parse(savedMenuSettings);
        Object.entries(menuSettings).forEach(([id, checked]) => {
          const checkbox = document.getElementById(id);
          if (checkbox) {
            checkbox.checked = checked;
          }
        });
      }
      
      // 비디오 자동 재생 설정 불러오기
      const savedAutoPlay = localStorage.getItem('autoPlay');
      const autoPlaySwitch = document.getElementById('autoPlaySwitch');
      if (savedAutoPlay && autoPlaySwitch) {
        autoPlaySwitch.checked = savedAutoPlay === 'true';
      }
    } catch (error) {
      console.error('설정 불러오기 실패:', error);
    }
  };
  
  // 페이지 로드 시 설정 불러오기
  window.addEventListener('DOMContentLoaded', loadSettings);
};

// 패널 관련 이벤트 리스너 설정
function setupPanelEventListeners() {
  // 설정에서 기존 패널 복원
  try {
    // 패널 매니저 설정 로드 (패널 자동 복원)
    console.log('웹 패널 설정을 로드합니다...');
  } catch (error) {
    console.error('웹 패널 설정 로드 중 오류 발생:', error);
  }
  
  // FAB 버튼 관련 요소
  const fabContainer = document.getElementById('fab-container');
  const fabMainButton = document.getElementById('fab-main-button');
  const openPanelManager = document.getElementById('open-panel-manager');
  
  // 패널 관리 메뉴 및 버튼 관련 요소
  const panelManagerMenu = document.getElementById('panel-manager-menu');
  const panelManagerClose = document.getElementById('panel-manager-close');
  const createPanelMenu = document.getElementById('create-panel-menu');
  const exportPanelsConfig = document.getElementById('export-panels-config');
  const importPanelsConfig = document.getElementById('import-panels-config');
  const panelConfigFile = document.getElementById('panel-config-file');
  const closeAllPanels = document.getElementById('close-all-panels');
  
  // FAB 메인 버튼 토글
  if (fabMainButton && fabContainer) {
    let isOpen = false;
    
    fabMainButton.addEventListener('click', function() {
      isOpen = !isOpen;
      if (isOpen) {
        fabContainer.classList.add('open');
        fabMainButton.classList.add('active');
        fabMainButton.setAttribute('aria-expanded', 'true');
      } else {
        fabContainer.classList.remove('open');
        fabMainButton.classList.remove('active');
        fabMainButton.setAttribute('aria-expanded', 'false');
        
        // 메뉴가 닫히면 메인 버튼에 포커스 되돌리기
        if (document.activeElement) {
          // NodeList.contains() 메서드 대신 DOM에서 현재 포커스된 요소가 fabMainButton 내부인지 확인
          const activeElement = document.activeElement;
          const isChildOfMainButton = fabMainButton.contains(activeElement);
          
          if (isChildOfMainButton) {
            fabMainButton.focus();
          }
        }
      }
    });
    
    // FAB 외부 클릭 시 닫기
    document.addEventListener('click', (e) => {
      if (!fabContainer.contains(e.target)) {
        isOpen = false;
        fabContainer.classList.remove('open');
        fabMainButton.classList.remove('active');
        fabMainButton.setAttribute('aria-expanded', 'false');
      }
    });
  }
  
  // 패널 관리 메뉴 열기
  if (openPanelManager && panelManagerMenu) {
    openPanelManager.addEventListener('click', (e) => {
      e.stopPropagation(); // 이벤트 버블링 방지
      panelManagerMenu.classList.add('show');
      fabMainButton.setAttribute('aria-expanded', 'false');
      fabMainButton.classList.remove('active');
    });
  }
  
  // 패널 관리 메뉴 닫기
  if (panelManagerClose && panelManagerMenu) {
    panelManagerClose.addEventListener('click', () => {
      panelManagerMenu.classList.remove('show');
    });
    
    // 패널 관리 메뉴 외부 클릭 시 닫기
    document.addEventListener('click', (e) => {
      if (panelManagerMenu.classList.contains('show') && 
          !panelManagerMenu.contains(e.target) && 
          !openPanelManager.contains(e.target)) {
        panelManagerMenu.classList.remove('show');
      }
    });
  }
  
  // 패널 관리 메뉴에서 새 패널 생성
  if (createPanelMenu) {
    createPanelMenu.addEventListener('click', () => {
      createNewPanel('');
      panelManagerMenu.classList.remove('show');
      showToast('새 웹 패널이 생성되었습니다.', 'success');
    });
  }
  
  // 설정 내보내기 버튼
  if (exportPanelsConfig) {
    exportPanelsConfig.addEventListener('click', () => {
      if (panelManager.panels.length === 0) {
        alert('저장할 웹 패널이 없습니다.');
        return;
      }
      
      panelManager.exportConfigToJson();
      showToast('웹 패널 설정이 내보내기 되었습니다.', 'success');
    });
  }
  
  // 설정 가져오기 버튼
  if (importPanelsConfig && panelConfigFile) {
    importPanelsConfig.addEventListener('click', () => {
      panelConfigFile.click();
    });
    
    // 파일 선택 시 설정 가져오기
    panelConfigFile.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (file) {
        panelManager.importConfigFromJson(file)
          .then(() => {
            // 파일 선택 초기화
            panelConfigFile.value = '';
            showToast('웹 패널 설정이 가져오기 되었습니다.', 'success');
          })
          .catch(error => {
            console.error('설정 파일 가져오기 실패:', error);
            panelConfigFile.value = '';
            showToast('웹 패널 설정 가져오기에 실패했습니다.', 'error');
          });
      }
    });
  }
  
  // 모든 패널 닫기 버튼
  if (closeAllPanels) {
    closeAllPanels.addEventListener('click', () => {
      if (panelManager.panels.length === 0) {
        alert('닫을 웹 패널이 없습니다.');
        return;
      }
      
      if (confirm('모든 웹 패널을 닫으시겠습니까?')) {
        panelManager.closeAllPanels();
        panelManagerMenu.classList.remove('show');
        showToast('모든 웹 패널이 닫혔습니다.', 'info');
      }
    });
  }
  
  // 상단 메뉴 웹 패널 버튼
  const webPanelBtn = document.getElementById('create-web-panel');
  if (webPanelBtn) {
    webPanelBtn.addEventListener('click', (e) => {
      e.preventDefault();
      
      // 앱 내 네비게이션 플래그 설정
      if (window.appNavigation) {
        window.appNavigation.internalNavigation = true;
        window.appNavigation.lastState = 'SNS3_webpanel';
      }
      
      // 토스트 메시지 표시
      showToast('새 웹 패널이 생성되었습니다.', 'success');
      
      // 프롬프트 없이 빈 URL로 패널 생성
      createNewPanel('');
    });
  }
  
  // 사이드바 웹 패널 버튼
  const sidebarPanelBtn = document.getElementById('create-panel-sidebar');
  if (sidebarPanelBtn) {
    sidebarPanelBtn.addEventListener('click', (e) => {
      e.preventDefault();
      
      // 앱 내 네비게이션 플래그 설정
      if (window.appNavigation) {
        window.appNavigation.internalNavigation = true;
        window.appNavigation.lastState = 'SNS3_webpanel';
      }
      
      // 토스트 메시지 표시
      showToast('새 웹 패널이 생성되었습니다.', 'success');
      
      // 프롬프트 없이 빈 URL로 패널 생성
      createNewPanel('');
    });
  }
  
  // 플로팅 버튼
  const floatingBtn = document.getElementById('floating-panel-btn');
  if (floatingBtn) {
    floatingBtn.addEventListener('click', (e) => {
      e.stopPropagation(); // 이벤트 버블링 방지
      
      // 앱 내 네비게이션 플래그 설정
      if (window.appNavigation) {
        window.appNavigation.internalNavigation = true;
        window.appNavigation.lastState = 'SNS3_webpanel';
      }
      
      // 토스트 메시지 표시
      showToast('새 웹 패널이 생성되었습니다.', 'success');
      
      // 프롬프트 없이 빈 URL로 패널 생성
      createNewPanel('');
      
      // FAB 메뉴 닫기
      if (fabMainButton) {
        fabMainButton.setAttribute('aria-expanded', 'false');
        fabMainButton.classList.remove('active');
      }
    });
  }
}

// 새 패널 생성 함수
function createNewPanel(defaultUrl = '') {
  // 모바일 환경 감지
  const isMobile = window.innerWidth < 768;
  
  // 화면 중앙에 위치 계산
  const x = Math.max(10, (window.innerWidth - (isMobile ? window.innerWidth * 0.9 : 400)) / 2);
  const y = Math.max(10, (window.innerHeight - (isMobile ? window.innerHeight * 0.7 : 300)) / 2);
  
  // 랜덤 오프셋 추가 (여러 패널 생성 시 겹치지 않도록)
  // 모바일에서는 화면이 작으므로 오프셋을 적용하지 않음
  const randomOffsetX = isMobile ? 0 : Math.round(Math.random() * 40 - 20);
  const randomOffsetY = isMobile ? 0 : Math.round(Math.random() * 40 - 20);
  
  // 패널 크기 설정
  const width = isMobile ? window.innerWidth * 0.9 : 400;
  const height = isMobile ? window.innerHeight * 0.7 : 300;
  
  // URL이 제공된 경우 패널 제목 설정
  let title = '웹 패널';
  if (defaultUrl) {
    try {
      // 유효한 URL인 경우 도메인을 제목으로 사용
      const urlObj = new URL(defaultUrl.startsWith('http') ? defaultUrl : `https://${defaultUrl}`);
      title = urlObj.hostname || '웹 패널';
    } catch (e) {
      // URL 파싱에 실패하면 기본 제목 사용
      console.log('URL 파싱 실패, 기본 제목 사용:', e);
    }
  }
  
  // 패널 생성
  const panel = panelManager.createPanel({
    title: title,
    width: width,
    height: height,
    x: x + randomOffsetX,
    y: y + randomOffsetY,
    defaultUrl: defaultUrl
  });
  
  // 설정 자동 저장 (패널 생성은 이미 panelManager.createPanel 내부에서 저장)
  return panel;
}

// FAB 버튼 관련 이벤트 리스너
const setupFabButtons = () => {
  const fabMainButton = document.getElementById('fab-main-button');
  const fabContainer = document.getElementById('fab-container');
  
  if (fabMainButton && fabContainer) {
    // fabSubButtons를 ID가 아닌 클래스 선택자로 찾습니다
    const fabSubButtons = fabContainer.querySelector('.fab-sub-buttons');
    
    if (!fabSubButtons) {
      console.error('FAB 서브 버튼 컨테이너를 찾을 수 없습니다');
      return;
    }
    
    let isOpen = false;
    
    fabMainButton.addEventListener('click', function() {
      isOpen = !isOpen;
      if (isOpen) {
        fabContainer.classList.add('open');
        fabMainButton.classList.add('active');
        fabMainButton.setAttribute('aria-expanded', 'true');
      } else {
        fabContainer.classList.remove('open');
        fabMainButton.classList.remove('active');
        fabMainButton.setAttribute('aria-expanded', 'false');
        
        // 메뉴가 닫히면 메인 버튼에 포커스 되돌리기
        if (document.activeElement) {
          // NodeList.contains() 메서드 대신 DOM에서 현재 포커스된 요소가 fabMainButton 내부인지 확인
          const activeElement = document.activeElement;
          const isChildOfMainButton = fabMainButton.contains(activeElement);
          
          if (isChildOfMainButton) {
            fabMainButton.focus();
          }
        }
      }
    });

    // 각 서브 버튼에 이벤트 리스너 추가
    const subButtonsList = fabSubButtons.querySelectorAll('.fab-sub-button');
    subButtonsList.forEach(button => {
      button.addEventListener('click', function() {
        // 서브 버튼 클릭 시 FAB 닫기
        isOpen = false;
        fabContainer.classList.remove('open');
        fabMainButton.setAttribute('aria-expanded', 'false');
      });
    });
  }
};

// FAB 버튼 설정 함수 호출
setupFabButtons();

// --- 게시물 드래그 앤 드롭 핸들러 --- 

function handlePostDragOver(event) {
  event.preventDefault(); // 드롭을 허용하기 위해 필수
  event.dataTransfer.dropEffect = 'move';
  // 시각적 피드백을 위해 클래스 추가
  event.currentTarget.classList.add('drag-over'); 
}

function handlePostDragLeave(event) {
  // 드래그 요소가 영역을 벗어나면 시각적 피드백 제거
  event.currentTarget.classList.remove('drag-over'); 
}

function handlePostDrop(event) {
  event.preventDefault();
  event.currentTarget.classList.remove('drag-over');

  const panelId = event.dataTransfer.getData('text/plain');
  const postElement = event.currentTarget.closest('.post-card');
  const postId = postElement ? postElement.dataset.postId : null;

  if (!panelId || !postId) {
    console.error('드롭 데이터에서 패널 ID 또는 게시물 ID를 찾을 수 없습니다.');
    return;
  }

  // DB 패널인지 일반 패널인지 확인
  let panelInstance;
  let panelManager;
  
  // 먼저 일반 패널에서 찾기
  if (window.panelManager) {
    panelManager = window.panelManager;
    panelInstance = panelManager.getPanelById(panelId);
  }
  
  // 일반 패널에서 찾지 못한 경우 DB 패널에서 찾기
  if (!panelInstance && window.dbPanelManager) {
    panelManager = window.dbPanelManager;
    panelInstance = panelManager.getPanelById(panelId);
  }

  if (!panelInstance) {
    console.error('ID에 해당하는 패널 인스턴스를 찾을 수 없습니다:', panelId);
    return;
  }

  // 패널 정보 및 HTML 생성
  const panelConfig = panelInstance.getConfig ? panelInstance.getConfig() : { id: panelId };
  const embedHtmlString = panelInstance.toHTMLEmbed();

  // DOMParser를 사용하여 안전하게 HTML 파싱
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${embedHtmlString}</div>`, 'text/html');
  const embedElement = doc.body.firstChild.firstChild;

  // 삽입할 컨테이너 생성
  const embedContainer = document.createElement('div');
  embedContainer.classList.add('embedded-panel-in-post');
  embedContainer.appendChild(embedElement);

  // 드롭된 위치(게시물 본문)에 컨테이너 추가
  event.currentTarget.appendChild(embedContainer);
  console.log(`패널 ${panelId}가 게시물 ${postId} 본문에 드롭되었습니다.`);

  // DB 패널과 일반 패널 구분하여 저장
  const panelType = panelInstance.isDBPanel ? 'db' : 'web';
  const panelDataToSave = {
    id: panelId,
    type: panelType,
    title: panelConfig.title || (panelType === 'db' ? 'DB 패널' : '웹 패널')
  };
  
  // 웹 패널의 경우 URL 저장
  if (panelType === 'web' && panelConfig.url) {
    panelDataToSave.url = panelConfig.url;
  }
  
  // DB 패널의 경우 선택된 디바이스 정보 저장
  if (panelType === 'db' && panelInstance.deviceSelect && panelInstance.deviceSelect.value) {
    panelDataToSave.deviceId = panelInstance.deviceSelect.value;
    panelDataToSave.deviceName = panelInstance.deviceSelect.options[panelInstance.deviceSelect.selectedIndex].text;
  }
  
  addEmbeddedPanelToPostData(postId, panelDataToSave);

  // 패널 닫기
  panelInstance.close();
}

// 전역 스코프에 핸들러 함수들을 할당하여 다른 모듈에서 사용할 수 있도록 함 (또는 모듈 시스템 사용)
window.handlePostDragOver = handlePostDragOver;
window.handlePostDragLeave = handlePostDragLeave;
window.handlePostDrop = handlePostDrop;

/**
 * 알림 이벤트 리스너 설정
 */
function setupAlertNotificationListeners() {
  const currentUser = auth.currentUser;
  if (!currentUser) return;
  
  // 알림 권한 요청 버튼 이벤트
  const notificationPermissionBtn = document.getElementById('notification-permission-btn');
  if (notificationPermissionBtn) {
    notificationPermissionBtn.addEventListener('click', async () => {
      const token = await requestNotificationPermission();
      if (token) {
        showToast('알림이 성공적으로 활성화되었습니다.', 'success');
        notificationPermissionBtn.classList.add('d-none');
      } else {
        showToast('알림 권한을 허용해 주세요.', 'warning');
      }
    });
  }
  
  // 사용자 알림 확인
  const notificationsRef = ref(database, `users/${currentUser.uid}/notifications`);
  onValue(notificationsRef, async (snapshot) => {
    if (!snapshot.exists()) return;
    
    const notifications = snapshot.val();
    const unreadNotifications = Object.entries(notifications)
      .filter(([key, notification]) => !notification.read)
      .sort((a, b) => new Date(b[1].timestamp) - new Date(a[1].timestamp));
    
    // 알림 배지 업데이트
    updateNotificationBadge(unreadNotifications.length);
    
    // 첫 번째 읽지 않은 채팅 알림 처리 (사용자가 현재 사이트에 있을 때)
    if (unreadNotifications.length > 0 && document.visibilityState === 'visible') {
      const [notificationId, notification] = unreadNotifications[0];
      
      if (notification.type === 'chat') {
        // 사용자가 해당 채팅방을 이미 열었는지 확인
        const openPanels = panelManager.panels;
        const isChatRoomOpen = openPanels.some(panel => {
          const chatRoomElement = panel.panel.querySelector(`[data-chat-room-id="${notification.chatRoomId}"]`);
          return chatRoomElement !== null;
        });
        
        if (!isChatRoomOpen) {
          // 브라우저 알림 표시
          if (Notification.permission === 'granted') {
            const notif = new Notification(notification.title, {
              body: notification.body,
              icon: '/assets/icons/icon-192x192.png'
            });
            
            notif.onclick = () => {
              window.focus();
              openChatRoomFromNotification(notification.chatRoomId);
              markNotificationAsRead(notificationId);
            };
          }
        } else {
          // 이미 채팅방이 열려 있으면 알림을 읽음으로 표시
          markNotificationAsRead(notificationId);
        }
      }
    }
  });
}

/**
 * 알림 배지 업데이트
 * @param {number} count - 알림 개수
 */
function updateNotificationBadge(count) {
  // 네비게이션 바의 알림 아이콘 배지 업데이트
  const notificationNavItem = document.querySelector('.nav-item a[href="#"] i.bi-bell');
  if (notificationNavItem) {
    // 기존 배지 제거
    const existingBadge = notificationNavItem.parentElement.querySelector('.chat-badge');
    if (existingBadge) {
      existingBadge.remove();
    }
    
    // 새 배지 추가 (알림이 있는 경우)
    if (count > 0) {
      const badge = document.createElement('span');
      badge.className = 'chat-badge';
      badge.textContent = count > 99 ? '99+' : count;
      notificationNavItem.parentElement.appendChild(badge);
    }
  }
}

/**
 * 알림을 읽음으로 표시
 * @param {string} notificationId - 알림 ID
 */
async function markNotificationAsRead(notificationId) {
  const currentUser = auth.currentUser;
  if (!currentUser) return;
  
  try {
    const notificationRef = ref(database, `users/${currentUser.uid}/notifications/${notificationId}`);
    await update(notificationRef, { read: true });
    console.log(`알림 ${notificationId}를 읽음으로 표시했습니다.`);
  } catch (error) {
    console.error('알림 상태 업데이트 오류:', error);
  }
}

/**
 * 알림에서 채팅방 열기
 * @param {string} chatRoomId - 채팅방 ID
 */
async function openChatRoomFromNotification(chatRoomId) {
  try {
    // 채팅방 정보 가져오기
    const chatRoomRef = ref(database, `chatingRooms/${chatRoomId}`);
    const chatRoomSnapshot = await get(chatRoomRef);
    
    if (chatRoomSnapshot.exists()) {
      // 게시물 정보 가져오기
      const chatRoom = chatRoomSnapshot.val();
      const postId = chatRoom.postId;
      
      if (postId) {
        const postRef = ref(database, `posts/${postId}`);
        const postSnapshot = await get(postRef);
        
        if (postSnapshot.exists()) {
          const post = postSnapshot.val();
          post.id = postId;
          
          // 게시물 컨테이너 숨기기
          const postsContainer = document.getElementById('posts-container');
          if (postsContainer) {
            postsContainer.style.display = 'none';
          }
          
          // 채팅 UI 컨테이너 생성 또는 표시
          let chatUIContainer = document.querySelector('.whatsapp-style-chat');
          if (!chatUIContainer) {
            chatUIContainer = document.createElement('div');
            chatUIContainer.id = 'chat-ui-container';
            chatUIContainer.className = 'whatsapp-style-chat';
            
            // 채팅 UI를 게시물 컨테이너의 부모 요소에 추가
            postsContainer.parentNode.appendChild(chatUIContainer);
          } else {
            chatUIContainer.style.display = 'flex';
          }
          
          console.log('채팅 UI 생성 (event-listeners.js)', chatRoomId);
          chatUIContainer.innerHTML = `
            <div class="chat-ui-header">
              <button id="back-to-posts-btn" class="btn btn-link">
                <i class="bi bi-arrow-left"></i>
              </button>
              <h3 class="chat-title">${post.content.substring(0, 20)}...</h3>
              <div class="chat-header-actions">
                <button id="chat-notification-toggle-${chatRoomId}" class="btn btn-link chat-action-btn chat-notification-toggle">
                  <i class="bi bi-bell"></i>
                </button>
                <button class="btn btn-link chat-action-btn">
                  <i class="bi bi-three-dots-vertical"></i>
                </button>
              </div>
            </div>
            <div id="chat-messages-${chatRoomId}" class="chat-messages-area">
              <div class="chat-loading">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">로딩 중...</span>
                </div>
              </div>
            </div>
            <div class="chat-input-area">
              <div class="chat-toolbar">
                <button class="btn btn-link chat-tool-btn">
                  <i class="bi bi-emoji-smile"></i>
                </button>
                <input type="file" id="chat-file-input-${chatRoomId}" class="d-none" accept="image/*,application/pdf,video/*,audio/*,text/*">
                <button id="chat-file-btn-${chatRoomId}" class="btn btn-link chat-tool-btn">
                  <i class="bi bi-paperclip"></i>
                </button>
              </div>
              <div class="chat-input-wrapper">
                <textarea id="chat-input-${chatRoomId}" class="chat-input" placeholder="메시지를 입력하세요..." rows="1"></textarea>
                <button id="chat-send-btn-${chatRoomId}" class="chat-send-btn">
                  <i class="bi bi-send"></i>
                </button>
              </div>
            </div>
          `;
          
          // 채팅방 ID 전역 저장 (리스너 해제용)
          window.currentChatRoomId = chatRoomId;
          
          // 뒤로가기 버튼 이벤트 리스너 설정
          document.getElementById('back-to-posts-btn').addEventListener('click', () => {
            chatUIContainer.style.display = 'none';
            postsContainer.style.display = 'block';
            // 채팅 메시지 리스너 해제
            off(ref(database, `chatingRooms/${chatRoomId}/messages`));
            
            // 채팅창 상태 업데이트
            window.isChatUIOpen = false;
            window.currentChatRoomId = null;
          });
          
          // 채팅 리스너 설정 함수 호출
          const { setupChatListenersForFullUI } = await import('../components/post.js');
          setupChatListenersForFullUI(chatRoomId);
          
          // 채팅창 상태 업데이트
          window.isChatUIOpen = true;
          
          // 모바일 뒤로가기 버튼 처리를 위해 더미 상태 추가
          history.pushState({ chatRoom: chatRoomId }, '', window.location.href);
          
          showToast('채팅방에 입장했습니다.', 'success');
        } else {
          showToast('게시물을 찾을 수 없습니다.', 'error');
        }
      } else {
        showToast('채팅방에 연결된 게시물이 없습니다.', 'error');
      }
    } else {
      showToast('존재하지 않는 채팅방입니다.', 'error');
    }
  } catch (error) {
    console.error('채팅방 열기 오류:', error);
    showToast('채팅방을 여는 중 오류가 발생했습니다.', 'error');
  }
}

/**
 * FCM 토큰을 Firebase에 저장하는 함수
 */
const saveFCMToken = async () => {
  if (!auth.currentUser) return;
  
  try {
    // FCM 토큰 요청
    const fcmToken = await requestNotificationPermission();
    
    if (!fcmToken) {
      console.log('FCM 토큰을 가져올 수 없습니다.');
      return;
    }
    
    // 사용자의 FCM 토큰 저장
    const tokenRef = ref(database, `users/${auth.currentUser.uid}/fcmTokens/${fcmToken.replace(/[.#$/\[\]]/g, '_')}`);
    await set(tokenRef, fcmToken);
    
    // 토큰 정보에 마지막 업데이트 시간 추가
    const userTokensMetaRef = ref(database, `users/${auth.currentUser.uid}/fcmTokensMeta`);
    await update(userTokensMetaRef, {
      lastUpdated: new Date().toISOString(),
      platform: 'web',
      userAgent: navigator.userAgent
    });
    
    console.log('FCM 토큰이 저장되었습니다.');
  } catch (error) {
    console.error('FCM 토큰 저장 오류:', error);
  }
};

/**
 * 드래그 가능한 패널 토글 함수
 * 바로가기 메뉴에서 웹 패널 열기 버튼 클릭 시 호출됨
 */
function toggleDraggablePanel() {
  // 새 패널 생성 (빈 URL로)
  createNewPanel('');
}

/**
 * 투어 가이드 이벤트 리스너 설정
 */
const setupTourGuideListeners = () => {
  // 푸터에 투어 버튼 추가
  const appVersionEl = document.getElementById('app-version');
  if (appVersionEl) {
    const tourButton = document.createElement('button');
    tourButton.id = 'start-tour-btn';
    tourButton.className = 'btn btn-sm btn-link text-muted p-0 ms-2';
    tourButton.textContent = '앱 투어 시작';
    tourButton.addEventListener('click', () => {
      // 투어 메뉴 모달 표시
      showTourMenu();
    });
    
    appVersionEl.appendChild(tourButton);
  }
  
  // 설정 모달의 기타 탭에 투어 버튼 추가
  const otherTab = document.getElementById('other-tab');
  if (otherTab) {
    otherTab.addEventListener('shown.bs.tab', () => {
      const otherTabContent = document.getElementById('other');
      if (otherTabContent) {
        // 이미 투어 버튼이 있는지 확인
        if (!document.getElementById('settings-tour-btn')) {
          const tourButtonGroup = document.createElement('div');
          tourButtonGroup.className = 'settings-btn-group mt-3';
          tourButtonGroup.innerHTML = `
            <button id="settings-tour-btn" class="btn btn-primary">
              <i class="bi bi-info-circle"></i> 앱 투어 시작
            </button>
            <button id="reset-tour-btn" class="btn btn-outline-secondary">
              <i class="bi bi-arrow-repeat"></i> 투어 기록 초기화
            </button>
          `;
          
          otherTabContent.insertBefore(tourButtonGroup, otherTabContent.querySelector('.form-group.mt-4'));
          
          // 투어 시작 버튼 이벤트
          document.getElementById('settings-tour-btn').addEventListener('click', () => {
            // 모달 닫기
            const settingsModal = bootstrap.Modal.getInstance(document.getElementById('settingsModal'));
            if (settingsModal) {
              settingsModal.hide();
            }
            
            // 투어 메뉴 모달 표시
            showTourMenu();
          });
          
          // 투어 기록 초기화 버튼 이벤트
          document.getElementById('reset-tour-btn').addEventListener('click', () => {
            tourGuide.resetTourHistory();
            showToast('투어 기록이 초기화되었습니다.', 'success');
          });
        }
      }
    });
  }
};

/**
 * 투어 메뉴 모달 표시
 */
const showTourMenu = () => {
  // 이미 존재하는 모달인지 확인
  let tourMenuModal = document.getElementById('tourMenuModal');
  
  if (!tourMenuModal) {
    // 모달 생성
    tourMenuModal = document.createElement('div');
    tourMenuModal.className = 'modal fade';
    tourMenuModal.id = 'tourMenuModal';
    tourMenuModal.tabIndex = '-1';
    tourMenuModal.setAttribute('aria-labelledby', 'tourMenuModalLabel');
    tourMenuModal.setAttribute('aria-hidden', 'true');
    
    tourMenuModal.innerHTML = `
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="tourMenuModalLabel">투어 가이드</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p>원하는 기능에 대한 투어를 선택하세요:</p>
            <div class="list-group">
              <button type="button" class="list-group-item list-group-item-action" id="home-tour-btn">
                <i class="bi bi-house-door me-2"></i>홈 화면 투어
              </button>
              <button type="button" class="list-group-item list-group-item-action" id="post-tour-btn">
                <i class="bi bi-file-text me-2"></i>게시물 기능 투어
              </button>
              <button type="button" class="list-group-item list-group-item-action" id="permissions-tour-btn">
                <i class="bi bi-shield-lock me-2"></i>권한 관리 투어
              </button>
              <button type="button" class="list-group-item list-group-item-action" id="panel-tour-btn">
                <i class="bi bi-window me-2"></i>웹 패널 투어
              </button>
              <button type="button" class="list-group-item list-group-item-action" id="settings-tour-btn-modal">
                <i class="bi bi-gear me-2"></i>설정 투어
              </button>
            </div>
            <div class="form-check mt-3">
              <input class="form-check-input" type="checkbox" id="auto-advance-check">
              <label class="form-check-label" for="auto-advance-check">
                자동으로 투어 진행 (3초 간격)
              </label>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">닫기</button>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(tourMenuModal);
    
    // 투어 버튼에 이벤트 리스너 추가
    document.getElementById('home-tour-btn').addEventListener('click', () => {
      const modal = bootstrap.Modal.getInstance(tourMenuModal);
      const autoAdvance = document.getElementById('auto-advance-check').checked;
      if (modal) modal.hide();
      tourGuide.startHomeTour(autoAdvance);
    });
    
    document.getElementById('post-tour-btn').addEventListener('click', () => {
      const modal = bootstrap.Modal.getInstance(tourMenuModal);
      const autoAdvance = document.getElementById('auto-advance-check').checked;
      if (modal) modal.hide();
      tourGuide.startPostTour(autoAdvance);
    });
    
    document.getElementById('permissions-tour-btn').addEventListener('click', () => {
      const modal = bootstrap.Modal.getInstance(tourMenuModal);
      const autoAdvance = document.getElementById('auto-advance-check').checked;
      if (modal) modal.hide();
      tourGuide.startPermissionsTour(autoAdvance);
    });
    
    document.getElementById('panel-tour-btn').addEventListener('click', () => {
      const modal = bootstrap.Modal.getInstance(tourMenuModal);
      const autoAdvance = document.getElementById('auto-advance-check').checked;
      if (modal) modal.hide();
      tourGuide.startPanelTour(autoAdvance);
    });
    
    document.getElementById('settings-tour-btn-modal').addEventListener('click', () => {
      const modal = bootstrap.Modal.getInstance(tourMenuModal);
      const autoAdvance = document.getElementById('auto-advance-check').checked;
      if (modal) modal.hide();
      tourGuide.startSettingsTour(autoAdvance);
    });
  }
  
  // 모달 표시
  new bootstrap.Modal(tourMenuModal).show();
};

/**
 * 업데이트 확인 버튼 및 자동 업데이트 확인 기능 설정
 */
const setupUpdateChecker = () => {
  // 업데이트 확인 버튼 이벤트 리스너
  const checkUpdatesBtn = document.getElementById('check-updates-btn');
  if (checkUpdatesBtn) {
    checkUpdatesBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      
      // 버튼에 로딩 상태 표시
      const originalHTML = checkUpdatesBtn.innerHTML;
      checkUpdatesBtn.innerHTML = '<i class="bi bi-arrow-repeat spin"></i> 확인 중...';
      checkUpdatesBtn.disabled = true;
      
      try {
        // 업데이트 확인 함수 호출
        await checkForUpdates();
      } catch (error) {
        console.error('업데이트 확인 중 오류 발생:', error);
        showToast('업데이트 확인 중 오류가 발생했습니다.', 'error');
      } finally {
        // 버튼 상태 복원
        setTimeout(() => {
          checkUpdatesBtn.innerHTML = originalHTML;
          checkUpdatesBtn.disabled = false;
        }, 1000);
      }
    });
  }
  
  // 주기적인 업데이트 확인 설정 (하루에 한 번)
  setupPeriodicUpdateCheck();
};

// DB 패널 버튼 이벤트 리스너 설정
const setupDBPanelEventListeners = () => {
  console.log("DB 패널 이벤트 리스너 설정 중...");

  // JSON Editor 라이브러리가 로드되었는지 확인
  if (typeof JSONEditor === 'undefined') {
    console.warn('JSONEditor 라이브러리가 로드되지 않았습니다. DB 패널 기능이 제한됩니다.');
  }

  // DB 패널 버튼 클릭 이벤트
  const createDBPanelBtn = document.getElementById('createDBPanelBtn');
  if (createDBPanelBtn) {
    createDBPanelBtn.addEventListener('click', async () => {
      console.log("DB 패널 생성 버튼 클릭됨");
      
      try {
        // Firebase 상태 확인
        if (!auth || !database) {
          console.error('Firebase 초기화 오류: auth 또는 database가 정의되지 않았습니다.');
          
          // Firebase 재초기화 시도
          try {
            const firebaseModule = await import('../utils/firebase-config.js');
            if (!firebaseModule.auth || !firebaseModule.database) {
              showToast('Firebase가 초기화되지 않았습니다.', 'error');
              return;
            }
          } catch (firebaseError) {
            console.error('Firebase 모듈 로드 실패:', firebaseError);
            showToast('Firebase를 초기화할 수 없습니다.', 'error');
            return;
          }
        }
        
        // 로그인 상태 확인
        if (!auth.currentUser) {
          showToast('먼저 로그인해주세요.', 'warning');
          return;
        }
        
        // DB 패널 매니저가 초기화되었는지 확인
        if (!window.dbPanelManager) {
          console.warn('DB 패널 매니저가 초기화되지 않았습니다. 동적 로드를 시도합니다.');
          
          // DB 패널 모듈 동적 로드 시도
          try {
            const { dbPanelManager } = await import('../components/db-panel.js');
            window.dbPanelManager = dbPanelManager;
            console.log('DB 패널 매니저가 동적으로 로드되었습니다.');
          } catch (moduleError) {
            console.error('DB 패널 모듈 로드 실패:', moduleError);
            showToast('DB 패널 모듈을 로드할 수 없습니다.', 'error');
            return;
          }
        }
        
        // DB 패널 생성
        const dbPanel = window.dbPanelManager.createDBPanel();
        showToast('DB 패널이 생성되었습니다.', 'success');
        
        // 드래그 앤 드롭 이벤트 활성화
        setupDBPanelDragEvents(dbPanel);
      } catch (error) {
        console.error('DB 패널 생성 오류:', error);
        showToast('DB 패널 생성 중 오류가 발생했습니다: ' + error.message, 'error');
      }
    });
  } else {
    console.warn('createDBPanelBtn 요소를 찾을 수 없습니다.');
  }
};

/**
 * DB 패널의 드래그 앤 드롭 이벤트 설정
 * @param {Object} dbPanel DB 패널 인스턴스
 */
const setupDBPanelDragEvents = (dbPanel) => {
  // 패널 헤더에 드래그 이벤트 추가
  const panelHeader = dbPanel.panel.querySelector('.panel-header');
  if (panelHeader) {
    panelHeader.setAttribute('draggable', 'true');
    
    panelHeader.addEventListener('dragstart', (event) => {
      // 드래그 데이터 설정
      event.dataTransfer.setData('text/plain', dbPanel.id);
      
      // 드래그 효과 설정
      event.dataTransfer.effectAllowed = 'copy';
      
      // 드래그 중인 패널 스타일 변경
      dbPanel.panel.classList.add('dragging');
      
      // 이미지 숨기기
      setTimeout(() => {
        event.target.style.opacity = '0.4';
      }, 0);
      
      // 게시물 본문 영역 강조
      document.querySelectorAll('.droppable-post-content').forEach(el => {
        el.classList.add('drop-highlight');
      });
    });
    
    panelHeader.addEventListener('dragend', (event) => {
      // 드래그 종료 시 스타일 원래대로
      dbPanel.panel.classList.remove('dragging');
      event.target.style.opacity = '1';
      
      // 게시물 본문 영역 강조 제거
      document.querySelectorAll('.droppable-post-content').forEach(el => {
        el.classList.remove('drop-highlight');
      });
    });
  }
};

// 내보낼 함수들
export {
  createNewPanel,
  markNotificationAsRead,
  openChatRoomFromNotification,
  toggleDraggablePanel
};